import { useState, useEffect } from 'react';
import { palette } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import { AddressAutocomplete } from '../../components/common/AddressAutocomplete';
import type { AddressResult } from '../../components/common/AddressAutocomplete';
import { MoneyInput } from '../../components/common/MoneyInput';
import { ClientPicker, type ClientSelection } from '../../components/common/ClientPicker';
import { DatePicker } from '../../components/common/DatePicker';
import { LABEL, INPUT } from './config';
import { SOURCE_TYPES } from './config';
import { PROJECT_TEMPLATES, TEMPLATE_INDUSTRIES } from '../../data/projectTemplates';
import type { TeamUser } from './hooks';
import type { Project } from '../../types';
import type { TemplatePhase } from '../../data/projectTemplates';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: Partial<Project>, phases: TemplatePhase[] | null, userId: string) => Promise<string | null>;
  users: TeamUser[];
  userId: string;
}

export function CreateProjectPanel({ isOpen, onClose, onCreate, users, userId }: Props) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  // Rule #33: full ClientSelection captured even though projects table
  // only persists client_id + client_name. phone/email surface in detail
  // panels via JOIN through clients.
  const [clientSelection, setClientSelection] = useState<ClientSelection | null>(null);
  const [templateSlug, setTemplateSlug] = useState('');
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  // captured per Rule #33; projects table has no place_id column today.
  const [placeId, setPlaceId] = useState('');
  void placeId;
  const [location, setLocation] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [budget, setBudget] = useState('');
  const [managerId, setManagerId] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset form on open; key-remount refactor out of scope for §1.7
      setName(''); setDesc(''); setClientId(null); setClientSelection(null); setTemplateSlug('');
      setAddress(''); setLatitude(null); setLongitude(null); setPlaceId('');
      setLocation(''); setStartDate(''); setEndDate(''); setBudget('');
      setManagerId(''); setSourceType(''); setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError(null);
    const template = templateSlug ? PROJECT_TEMPLATES.find(t => t.slug === templateSlug) : null;
    const id = await onCreate({
      name: name.trim(), description: desc || null,
      client_id: clientId, client_name: clientSelection?.name ?? null,
      address: address || null, location: location || null,
      latitude: latitude,
      longitude: longitude,
      start_date: startDate || null, target_end_date: endDate || null,
      // Q1.5 boundary: hook accepts number; budget is canonical after MoneyInput blur.
      budget: budget ? Number(budget) : null,
      source_type: sourceType || null,
      template_slug: templateSlug || null,
    }, template?.phases ?? null, userId);
    setSaving(false);
    if (id) onClose();
    else setError('Failed to create project');
  };

  const F: React.CSSProperties = { marginBottom: 14 };

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title="New Project" subtitle="Create a project">
      {error && <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, marginBottom: 16 }}>{error}</div>}

      <div style={F}><label style={LABEL}>Name *</label>
        <input style={INPUT} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Kitchen Renovation — Smith" />
      </div>

      <div style={F}><label style={LABEL}>Client</label>
        <ClientPicker
          value={clientId}
          onChange={(sel) => { setClientId(sel?.clientId ?? null); setClientSelection(sel); }}
        />
      </div>

      <div style={F}><label style={LABEL}>Template (optional)</label>
        <select style={INPUT} value={templateSlug} onChange={e => setTemplateSlug(e.target.value)}>
          <option value="">No template — blank project</option>
          {TEMPLATE_INDUSTRIES.map(ind => (
            <optgroup key={ind} label={ind}>
              {PROJECT_TEMPLATES.filter(t => t.industry === ind).map(t => (
                <option key={t.slug} value={t.slug}>{t.name}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div style={F}><label style={LABEL}>Description</label>
        <textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={desc} onChange={e => setDesc(e.target.value)} />
      </div>

      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Location</label><input style={INPUT} value={location} onChange={e => setLocation(e.target.value)} /></div>
        <div style={{ flex: 1 }}>
          <label style={LABEL}>Address</label>
          <AddressAutocomplete
            initialValue={address}
            onSelect={(r: AddressResult) => {
              setAddress(r.fullAddress);
              setLatitude(r.latitude);
              setLongitude(r.longitude);
              if (r.placeId) setPlaceId(r.placeId);
            }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Start date</label>
          <DatePicker value={startDate || null} onChange={(v) => setStartDate(v ?? '')} />
        </div>
        <div style={{ flex: 1 }}><label style={LABEL}>Target end date</label>
          <DatePicker value={endDate || null} onChange={(v) => setEndDate(v ?? '')} minDate={startDate || undefined} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Budget</label><MoneyInput value={budget} onChange={setBudget} /></div>
        <div style={{ flex: 1 }}><label style={LABEL}>Project manager</label>
          <select style={INPUT} value={managerId} onChange={e => setManagerId(e.target.value)}>
            <option value="">Unassigned</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
          </select>
        </div>
      </div>

      <div style={F}><label style={LABEL}>Source type</label>
        <select style={INPUT} value={sourceType} onChange={e => setSourceType(e.target.value)}>
          <option value="">—</option>
          {Object.entries(SOURCE_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={handleSubmit} disabled={saving} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: saving ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{saving ? 'Creating...' : 'Create Project'}</button>
      </div>
    </SlidePanel>
  );
}
